import {
  addCalendarDays,
  addInterval,
  intervalLabel,
  type RepeatAnchor,
  type RepeatBookWith,
  type RepeatEnds,
  type RepeatIntervalUnit,
} from '@fieldops/shared'

/**
 * A repeat rule as the form holds it. Decision 96.
 *
 * The same shape starts a schedule from a job, edits one, and adds a service
 * to an agreement, so the three cannot drift into three ideas of a rule.
 * Services carry their name so the list can be drawn before the catalogue has
 * arrived; `ruleInput` drops it on the way to the server.
 */
export type RuleDraft = {
  intervalCount: number
  intervalUnit: RepeatIntervalUnit
  anchor: RepeatAnchor
  bookWith: RepeatBookWith
  bookWithUserId: string | null
  ends: RepeatEnds
  endAfterVisits: number | null
  endsOn: string | null
  categoryId: string | null
  estimatedMinutes: number | null
  standingNotes: string
  services: RuleService[]
}

export type RuleService = { serviceTypeId: string; name: string; priceIncTax: number | null }

export const DEFAULT_RULE: RuleDraft = {
  intervalCount: 6,
  intervalUnit: 'MONTH',
  anchor: 'DUE_DATE',
  bookWith: 'LAST_TECH',
  bookWithUserId: null,
  ends: 'NEVER',
  endAfterVisits: null,
  endsOn: null,
  categoryId: null,
  estimatedMinutes: null,
  standingNotes: '',
  services: [],
}

/** The rule fields every repeat procedure takes. */
export function ruleInput(draft: RuleDraft) {
  return {
    intervalCount: draft.intervalCount,
    intervalUnit: draft.intervalUnit,
    anchor: draft.anchor,
    bookWith: draft.bookWith,
    bookWithUserId: draft.bookWith === 'PERSON' ? draft.bookWithUserId : null,
    ends: draft.ends,
    endAfterVisits: draft.ends === 'AFTER_VISITS' ? draft.endAfterVisits : null,
    endsOn: draft.ends === 'ON_DATE' ? draft.endsOn : null,
    categoryId: draft.categoryId,
    estimatedMinutes: draft.estimatedMinutes,
    standingNotes: draft.standingNotes.trim() === '' ? null : draft.standingNotes.trim(),
    services: draft.services.map((service) => ({
      serviceTypeId: service.serviceTypeId,
      priceIncTax: service.priceIncTax,
    })),
  }
}

/** What stops the form saving, in the words the schema would use. */
export function ruleProblem(draft: RuleDraft): string | null {
  if (!Number.isInteger(draft.intervalCount) || draft.intervalCount < 1) {
    return 'Say how often it repeats.'
  }
  if (draft.ends === 'AFTER_VISITS' && !draft.endAfterVisits) return 'Say how many visits.'
  if (draft.ends === 'ON_DATE' && !draft.endsOn) return 'Pick the last date.'
  if (draft.bookWith === 'PERSON' && !draft.bookWithUserId) return 'Pick who to book.'
  return null
}

/** "A", "A and B", "A, B and C". */
export function andList(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? 'Service'
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** "18 Mar 2027". A calendar day has no zone, so it is read at local noon. */
export function dayLabel(day: string, options: { year?: boolean } = {}): string {
  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    ...(options.year === false ? {} : { year: 'numeric' }),
  }).format(new Date(`${day}T12:00:00`))
}

/** "Mar", "Mar 27" — a grid column's heading. */
export function monthLabel(month: string, withYear = false): string {
  return new Intl.DateTimeFormat('en-AU', {
    month: 'short',
    ...(withYear ? { year: '2-digit' } : {}),
  }).format(new Date(`${month}-15T12:00:00`))
}

/** "Every 6 months" / "Monthly", lowercased for the middle of a sentence. */
export function everyLabel(rule: Pick<RuleDraft, 'intervalCount' | 'intervalUnit'>): string {
  const label = intervalLabel(rule)
  return label.startsWith('Every') ? `every${label.slice(5)}` : label.toLowerCase()
}

export function endsLabel(rule: Pick<RuleDraft, 'ends' | 'endAfterVisits' | 'endsOn'>): string {
  if (rule.ends === 'AFTER_VISITS' && rule.endAfterVisits) {
    return `After ${rule.endAfterVisits} visit${rule.endAfterVisits === 1 ? '' : 's'}`
  }
  if (rule.ends === 'ON_DATE' && rule.endsOn) return `On ${dayLabel(rule.endsOn)}`
  return 'Never'
}

export function anchorLabel(anchor: RepeatAnchor): string {
  return anchor === 'DUE_DATE' ? 'Due date' : 'Completed date'
}

/** "2 weeks", "4 weeks", "10 days". */
export function leadLabel(days: number): string {
  if (days === 0) return 'on the day'
  if (days % 7 === 0) return `${days / 7} week${days === 7 ? '' : 's'}`
  return `${days} day${days === 1 ? '' : 's'}`
}

/**
 * The footer sentence of the repeat popup: when the next visit falls and when
 * its job will be created, from the day this one is due.
 */
export function nextVisitSentence(draft: RuleDraft, fromDay: string, leadDays: number): string {
  if (draft.anchor === 'COMPLETED_DATE') {
    const unit = draft.intervalUnit === 'WEEK' ? 'week' : 'month'
    const span = draft.intervalCount === 1 ? `a ${unit}` : `${draft.intervalCount} ${unit}s`
    return `The next visit is due ${span} after this job is completed, and its job is created ${leadLabel(leadDays)} before.`
  }
  const next = addInterval(fromDay, draft)
  const created = addCalendarDays(next, -leadDays)
  return `Next visit ${dayLabel(next)}. Its job is created on ${dayLabel(created)}, ${leadLabel(leadDays)} before.`
}

/** Minor units, the way the rest of the app writes a price. */
export function servicesTotal(services: readonly { priceIncTax: number | null }[]): number {
  return services.reduce((sum, service) => sum + (service.priceIncTax ?? 0), 0)
}
